import { buildDailySummaryFromMeals, loadMealLogsForDate } from './mealLog'
import { supabase } from './supabaseClient'
import {
  buildRuleBasedDebrief,
  classifyPerformanceTier,
  findDueMeal,
  type SessionDebrief,
} from './sessionDebriefCore'
import type { GroceryItem, MealBlock, TrainingBlock } from './protocolTypes'

export type { DebriefActionId, SessionDebrief } from './sessionDebriefCore'

export type FetchSessionDebriefInput = {
  schedule_id: string
  set_progress_pct?: number
  session_rpe?: number | null
  injury_status?: string | null
  force_refresh?: boolean
  local_context?: LocalSessionDebriefContext
}

export type LocalSessionDebriefContext = {
  userId: string
  block: TrainingBlock
  dayNumber: number
  hasWearableConnected: boolean
  calendarSyncEnabled: boolean
  dietaryLabel?: string
  meals?: MealBlock[]
  grocery?: GroceryItem[]
}

function localDateNow(timezone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone ?? undefined,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

function localMinutesNow(timezone?: string | null): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone ?? undefined,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date())

  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 12)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
  return hour * 60 + minute
}

function isEdgeFunctionUnavailable(error: { message?: string } | null): boolean {
  const message = String(error?.message ?? '').toLowerCase()
  return (
    message.includes('failed to send a request to the edge function') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('404')
  )
}

export async function buildLocalSessionDebrief(
  input: FetchSessionDebriefInput,
  context: LocalSessionDebriefContext,
): Promise<{ debrief: SessionDebrief | null; error: string | null }> {
  const { block, dayNumber, hasWearableConnected, calendarSyncEnabled, userId, meals = [], grocery = [] } = context

  const { data: profile } = await supabase
    .from('profiles')
    .select('first_name, timezone, vegetarian')
    .eq('id', userId)
    .maybeSingle()

  const timezone = profile?.timezone ?? null
  const logDate = localDateNow(timezone)

  const [{ data: workoutLog }, mealLogsResult, metricsResult, groceryFetch] = await Promise.all([
    supabase
      .from('daily_workout_logs')
      .select('started_at, completed_at')
      .eq('user_id', userId)
      .eq('log_date', logDate)
      .maybeSingle(),
    loadMealLogsForDate(userId, logDate),
    supabase
      .from('health_metrics')
      .select('recovery_score, hrv_rmssd')
      .eq('user_id', userId)
      .eq('metric_date', logDate)
      .order('hrv_rmssd', { ascending: false })
      .limit(1)
      .maybeSingle(),
    grocery.length > 0
      ? Promise.resolve({ data: grocery, error: null })
      : supabase.from('grocery_manifest').select('is_secured, is_depleted').eq('user_id', userId),
  ])

  if (mealLogsResult.error) {
    return { debrief: null, error: mealLogsResult.error }
  }

  const dayMeals = meals.filter((meal) => meal.day_id === block.day_name_id)
  const nutrition = dayMeals.length > 0
    ? buildDailySummaryFromMeals(logDate, dayMeals, mealLogsResult.logs)
    : null

  const exercisesTotal = block.exercises.length
  const exercisesCompleted = block.exercises.filter((exercise) => exercise.is_completed).length
  const setProgressPct = Math.round(Math.max(0, Math.min(100, Number(input.set_progress_pct ?? 0))))
  const performanceTier = classifyPerformanceTier(setProgressPct, exercisesCompleted, exercisesTotal)

  let sessionDurationMinutes: number | null = null
  if (workoutLog?.started_at && workoutLog?.completed_at) {
    sessionDurationMinutes = Math.max(
      1,
      Math.round((Date.parse(String(workoutLog.completed_at)) - Date.parse(String(workoutLog.started_at))) / 60_000),
    )
  }

  const groceryRows = grocery.length > 0
    ? grocery
    : (groceryFetch.data ?? [])

  let protocolMeals: { block_name: string; dish_name: string; time_stamp: string; day_id: string }[] = meals.map((meal) => ({
    block_name: meal.block_name,
    dish_name: meal.dish_name,
    time_stamp: meal.time_stamp.slice(0, 5),
    day_id: meal.day_id,
  }))

  if (protocolMeals.length === 0) {
    const { data: mealProtocols } = await supabase
      .from('meal_protocols')
      .select('block_name, dish_name, time_stamp, day_id')
      .eq('user_id', userId)

    protocolMeals = (mealProtocols ?? []).map((meal) => ({
      block_name: String(meal.block_name ?? ''),
      dish_name: String(meal.dish_name ?? ''),
      time_stamp: String(meal.time_stamp ?? '').slice(0, 5),
      day_id: String(meal.day_id ?? ''),
    }))
  }

  const itemsOutstanding = groceryRows.filter((row) => !row.is_depleted && !row.is_secured).length

  const debrief = buildRuleBasedDebrief({
    first_name: profile?.first_name ?? null,
    block_title: block.block_title,
    focus_subtitle: block.focus_subtitle || block.block_title,
    day_number: dayNumber,
    dietary_label: context.dietaryLabel ?? (profile?.vegetarian ? 'Vegetarian' : 'Omnivore'),
    set_progress_pct: setProgressPct,
    exercises_completed: exercisesCompleted,
    exercises_total: exercisesTotal,
    performance_tier: performanceTier,
    session_rpe: input.session_rpe ?? null,
    injury_status: input.injury_status ?? null,
    session_duration_minutes: sessionDurationMinutes,
    has_wearable: hasWearableConnected,
    calendar_sync_enabled: calendarSyncEnabled,
    recovery_score: metricsResult.data?.recovery_score != null ? Number(metricsResult.data.recovery_score) : null,
    hrv_rmssd: metricsResult.data?.hrv_rmssd != null ? Number(metricsResult.data.hrv_rmssd) : null,
    nutrition,
    due_meal: findDueMeal(protocolMeals, mealLogsResult.logs, block.day_name_id, localMinutesNow(timezone)),
    shopping: {
      has_list: groceryRows.length > 0,
      items_outstanding: itemsOutstanding,
      items_depleted: groceryRows.filter((row) => row.is_depleted).length,
    },
  })

  return { debrief, error: null }
}

export async function fetchSessionDebrief(
  input: FetchSessionDebriefInput,
): Promise<{ debrief: SessionDebrief | null; error: string | null }> {
  const { local_context, ...remoteInput } = input
  const { data, error } = await supabase.functions.invoke('generate-session-debrief', { body: remoteInput })

  if (!error && !data?.error && data?.debrief) {
    return { debrief: data.debrief as SessionDebrief, error: null }
  }

  if (local_context && (error ? isEdgeFunctionUnavailable(error) : Boolean(data?.error))) {
    return buildLocalSessionDebrief(input, local_context)
  }

  if (error) return { debrief: null, error: error.message }
  if (data?.error) return { debrief: null, error: String(data.error) }
  return { debrief: (data?.debrief as SessionDebrief) ?? null, error: null }
}
